/* Atrium — typed, dependency-free Matrix Client-Server API wrapper.

   Ported from the prototype's matrix.js. Differences:
   - Strongly typed surface (see types/matrix.ts).
   - Transport throws MatrixError on failure — callers decide how to handle it.
     The old client swallowed errors silently; that hid real bugs (e.g. knocks
     that never sent). Nothing here is swallowed at the transport layer. */

import {
  MatrixError,
  type Discovery,
  type JoinedMembers,
  type MatrixEvent,
  type MatrixSession,
  type MessagesResponse,
  type RoomMessageContent,
  type StateEvent,
  type SyncResponse,
} from '@/types/matrix'

const SESSION_KEY = 'atrium.session.v1'
const DISCO_KEY = 'atrium.disco.v1'

function load<T>(key: string): T | null {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null') as T | null
  } catch {
    return null
  }
}
function save(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* storage full / disabled — non-fatal */
  }
}
function clearKey(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    /* non-fatal */
  }
}

interface ReqOpts {
  body?: unknown
  baseUrl?: string
  token?: string | null
}

class MatrixClient {
  private session: MatrixSession | null = load<MatrixSession>(SESSION_KEY)
  private disco: Discovery | null = load<Discovery>(DISCO_KEY)
  private syncToken: string | null = null
  private txnSeq = 0
  private mediaCache = new Map<string, string>() // mxc:// → blob: object URL

  /* ---- discovery via .well-known at the current origin ---- */
  async discover(): Promise<Discovery> {
    if (this.disco?.baseUrl) return this.disco
    let baseUrl: string | null = null
    let rtcFocus: string | null = null
    let floorSpace: string | null = null
    let callUrl: string | null = null
    let whiteboard: string | null = null
    try {
      const r = await fetch(location.origin + '/.well-known/matrix/client', { cache: 'no-store' })
      if (r.ok) {
        const wk = (await r.json()) as Record<string, any>
        baseUrl = wk['m.homeserver']?.base_url ?? null
        const foci: Array<{ type: string; livekit_service_url?: string }> = wk['org.matrix.msc4143.rtc_foci'] || []
        const lk = foci.find((f) => f.type === 'livekit')
        if (lk?.livekit_service_url) rtcFocus = lk.livekit_service_url
        floorSpace = wk['org.atrium.floor_space'] ?? null
        callUrl = wk['org.atrium.call_url'] ?? null
        whiteboard = wk['org.atrium.whiteboard_url'] ?? null
      }
    } catch {
      /* not deployed behind Caddy — fall through to host guess */
    }
    if (!floorSpace) {
      // The floor may have been created at runtime (Conductor setup) after the
      // .well-known was rendered — the Conductor's status endpoint is current.
      try {
        const r = await fetch(location.origin + '/atrium/api/floor', { cache: 'no-store' })
        if (r.ok) floorSpace = ((await r.json()) as { floor_space?: string }).floor_space || null
      } catch {
        /* conductor not reachable — floor stays undiscovered */
      }
    }
    if (!baseUrl) {
      const h = location.hostname
      const guess = h.startsWith('matrix.') ? h : 'matrix.' + h.replace(/^app\./, '')
      baseUrl = location.protocol + '//' + guess
    }
    this.disco = { baseUrl, rtcFocus, floorSpace, callUrl, whiteboard }
    save(DISCO_KEY, this.disco)
    return this.disco
  }

  /** Drop the cached discovery and re-discover — used after floor setup so the
   *  freshly created floor Space is picked up without a reload. */
  async refreshDiscovery(): Promise<Discovery> {
    this.disco = null
    clearKey(DISCO_KEY)
    return this.discover()
  }

  /* ---- core request ---- */
  private async req<T = unknown>(method: string, path: string, opts: ReqOpts = {}): Promise<T> {
    const base = opts.baseUrl || this.session?.baseUrl || this.disco?.baseUrl
    if (!base) throw new MatrixError('homeserver not discovered yet', { errcode: 'M_NO_HOMESERVER' })
    const url = base.replace(/\/$/, '') + path
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const token = opts.token !== undefined ? opts.token : this.session?.accessToken
    if (token) headers['Authorization'] = 'Bearer ' + token
    let res: Response
    try {
      res = await fetch(url, {
        method,
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      })
    } catch (e) {
      throw new MatrixError('network error reaching ' + base, { errcode: 'M_NETWORK', data: { cause: String(e) } })
    }
    const text = await res.text()
    let data: Record<string, any> = {}
    try {
      data = text ? JSON.parse(text) : {}
    } catch {
      data = { raw: text }
    }
    if (!res.ok) {
      throw new MatrixError(data.error || `${method} ${path} failed (${res.status})`, {
        errcode: data.errcode,
        httpStatus: res.status,
        data,
      })
    }
    return data as T
  }

  /* ---- session accessors ---- */
  hasSession(): boolean {
    return !!this.session?.accessToken
  }
  getSession(): MatrixSession | null {
    return this.session
  }
  userId(): string | undefined {
    return this.session?.userId
  }
  rtcFocus(): string | null {
    return this.disco?.rtcFocus ?? null
  }
  floorSpace(): string | null {
    return this.disco?.floorSpace ?? null
  }
  callUrl(): string | null {
    return this.disco?.callUrl ?? null
  }
  whiteboardUrl(): string | null {
    return this.disco?.whiteboard ?? null
  }

  /* ---- auth ---- */
  async loginPassword(user: string, password: string): Promise<MatrixSession> {
    const { baseUrl } = await this.discover()
    const data = await this.req<{ user_id: string; device_id: string; access_token: string }>(
      'POST',
      '/_matrix/client/v3/login',
      {
        baseUrl,
        token: null,
        body: {
          type: 'm.login.password',
          identifier: { type: 'm.id.user', user },
          password,
          initial_device_display_name: 'Atrium Web',
        },
      },
    )
    this.session = { baseUrl, userId: data.user_id, deviceId: data.device_id, accessToken: data.access_token }
    save(SESSION_KEY, this.session)
    return this.session
  }

  async ssoRedirect(): Promise<void> {
    const { baseUrl } = await this.discover()
    const back = location.origin + location.pathname + '#/login'
    location.href =
      baseUrl.replace(/\/$/, '') +
      '/_matrix/client/v3/login/sso/redirect?redirectUrl=' +
      encodeURIComponent(back)
  }

  async completeSSO(loginToken: string): Promise<MatrixSession> {
    const { baseUrl } = await this.discover()
    const data = await this.req<{ user_id: string; device_id: string; access_token: string }>(
      'POST',
      '/_matrix/client/v3/login',
      { baseUrl, token: null, body: { type: 'm.login.token', token: loginToken, initial_device_display_name: 'Atrium Web' } },
    )
    this.session = { baseUrl, userId: data.user_id, deviceId: data.device_id, accessToken: data.access_token }
    save(SESSION_KEY, this.session)
    return this.session
  }

  async logout(): Promise<void> {
    try {
      await this.req('POST', '/_matrix/client/v3/logout')
    } catch {
      /* best effort — we clear locally regardless */
    }
    this.clearSession()
  }

  /** Drop the local session without calling the server — used when the token is
   *  already dead (e.g. the homeserver was reset), so a /logout call would 401. */
  clearSession(): void {
    this.session = null
    this.syncToken = null
    clearKey(SESSION_KEY)
  }

  /* ---- profile ---- */
  async whoami(): Promise<{ user_id: string; device_id?: string }> {
    return this.req('GET', '/_matrix/client/v3/account/whoami')
  }
  async profile(userId: string): Promise<{ displayname?: string; avatar_url?: string }> {
    try {
      return await this.req('GET', `/_matrix/client/v3/profile/${encodeURIComponent(userId)}`)
    } catch {
      return {}
    }
  }
  async setDisplayName(name: string): Promise<void> {
    if (!this.session) throw new MatrixError('no session', { errcode: 'M_NO_SESSION' })
    await this.req('PUT', `/_matrix/client/v3/profile/${encodeURIComponent(this.session.userId)}/displayname`, {
      body: { displayname: name },
    })
  }
  /** Set (mxc:// URI) or clear (null) the account avatar. */
  async setAvatarUrl(mxc: string | null): Promise<void> {
    if (!this.session) throw new MatrixError('no session', { errcode: 'M_NO_SESSION' })
    await this.req('PUT', `/_matrix/client/v3/profile/${encodeURIComponent(this.session.userId)}/avatar_url`, {
      body: { avatar_url: mxc || '' },
    })
  }

  /* ---- account data (private, per-user) ---- */
  async getAccountData<T = Record<string, unknown>>(type: string): Promise<T | null> {
    if (!this.session) return null
    try {
      return await this.req<T>(
        'GET',
        `/_matrix/client/v3/user/${encodeURIComponent(this.session.userId)}/account_data/${encodeURIComponent(type)}`,
      )
    } catch (e) {
      if (e instanceof MatrixError && e.httpStatus === 404) return null
      throw e
    }
  }
  async setAccountData(type: string, content: unknown): Promise<void> {
    if (!this.session) throw new MatrixError('no session', { errcode: 'M_NO_SESSION' })
    await this.req(
      'PUT',
      `/_matrix/client/v3/user/${encodeURIComponent(this.session.userId)}/account_data/${encodeURIComponent(type)}`,
      { body: content },
    )
  }

  /* ---- media (the homeserver media repo; content addresses are mxc:// URIs) ---- */
  async uploadMedia(file: File | Blob, filename?: string): Promise<string> {
    if (!this.session) throw new MatrixError('no session', { errcode: 'M_NO_SESSION' })
    const base = this.session.baseUrl.replace(/\/$/, '')
    const name = filename || (file instanceof File ? file.name : 'upload')
    let res: Response
    try {
      res = await fetch(base + '/_matrix/media/v3/upload?filename=' + encodeURIComponent(name), {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + this.session.accessToken,
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      })
    } catch (e) {
      throw new MatrixError('network error uploading media', { errcode: 'M_NETWORK', data: { cause: String(e) } })
    }
    const data = (await res.json().catch(() => ({}))) as Record<string, any>
    if (!res.ok || !data.content_uri) {
      throw new MatrixError(data.error || `media upload failed (${res.status})`, {
        errcode: data.errcode,
        httpStatus: res.status,
      })
    }
    return data.content_uri as string
  }

  /** Resolve an mxc:// URI to a local blob: URL. Modern media download is
   *  authenticated — an <img src> can't send the header, so we fetch with the
   *  token and hand back an object URL (cached for the session). */
  async mediaObjectUrl(mxc: string): Promise<string | null> {
    if (!mxc.startsWith('mxc://') || !this.session) return null
    const hit = this.mediaCache.get(mxc)
    if (hit) return hit
    const base = this.session.baseUrl.replace(/\/$/, '')
    const [server, mediaId] = mxc.slice('mxc://'.length).split('/')
    if (!server || !mediaId) return null
    const paths = [
      `/_matrix/client/v1/media/download/${encodeURIComponent(server)}/${encodeURIComponent(mediaId)}`,
      `/_matrix/media/v3/download/${encodeURIComponent(server)}/${encodeURIComponent(mediaId)}`, // pre-1.11 fallback
    ]
    for (const p of paths) {
      try {
        const res = await fetch(base + p, { headers: { Authorization: 'Bearer ' + this.session.accessToken } })
        if (!res.ok) continue
        const url = URL.createObjectURL(await res.blob())
        this.mediaCache.set(mxc, url)
        return url
      } catch {
        /* try the next endpoint */
      }
    }
    return null
  }

  /* ---- rooms & state ---- */
  async roomState(roomId: string): Promise<StateEvent[]> {
    return this.req('GET', `/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/state`)
  }
  async stateEvent<T = Record<string, unknown>>(roomId: string, type: string, key = ''): Promise<T | null> {
    try {
      return await this.req<T>(
        'GET',
        `/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/state/${encodeURIComponent(type)}/${encodeURIComponent(key)}`,
      )
    } catch (e) {
      if (e instanceof MatrixError && e.httpStatus === 404) return null
      throw e
    }
  }
  async sendState(roomId: string, type: string, key: string, content: unknown): Promise<{ event_id: string }> {
    return this.req(
      'PUT',
      `/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/state/${encodeURIComponent(type)}/${encodeURIComponent(key)}`,
      { body: content },
    )
  }
  async joinedMembers(roomId: string): Promise<JoinedMembers> {
    try {
      return await this.req('GET', `/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/joined_members`)
    } catch {
      return { joined: {} }
    }
  }
  async joinRoom(roomIdOrAlias: string): Promise<{ room_id: string }> {
    return this.req('POST', `/_matrix/client/v3/join/${encodeURIComponent(roomIdOrAlias)}`, { body: {} })
  }
  /** Matrix room knock (knock_restricted team rooms): ask the people inside to let you in. */
  async knockRoom(roomIdOrAlias: string, reason?: string): Promise<{ room_id: string }> {
    return this.req('POST', `/_matrix/client/v3/knock/${encodeURIComponent(roomIdOrAlias)}`, {
      body: reason ? { reason } : {},
    })
  }
  async invite(roomId: string, userId: string): Promise<void> {
    await this.req('POST', `/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/invite`, {
      body: { user_id: userId },
    })
  }
  async joinedRooms(): Promise<string[]> {
    const res = await this.req<{ joined_rooms?: string[] }>('GET', '/_matrix/client/v3/joined_rooms')
    return res.joined_rooms || []
  }
  async leaveRoom(roomId: string): Promise<void> {
    try {
      await this.req('POST', `/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/leave`, { body: {} })
    } catch {
      /* best effort */
    }
  }

  /* ---- MatrixRTC ---- */
  async openIdToken(): Promise<{ access_token: string; matrix_server_name: string }> {
    if (!this.session) throw new MatrixError('no session', { errcode: 'M_NO_SESSION' })
    return this.req('POST', `/_matrix/client/v3/user/${encodeURIComponent(this.session.userId)}/openid/request_token`, {
      body: {},
    })
  }

  /* ---- sync + messaging ---- */
  // Raw JSON — URLSearchParams encodes it exactly once. (Pre-encoding here would
  // double-encode and Synapse would 400 the whole sync, silently killing
  // real-time chat AND to-device knocks.)
  private static readonly SYNC_FILTER = JSON.stringify({
    room: { timeline: { limit: 30 }, state: { lazy_load_members: true } },
    presence: { types: [] },
  })
  async sync(timeout = 30000): Promise<SyncResponse> {
    const p = new URLSearchParams()
    if (this.syncToken) {
      p.set('since', this.syncToken)
      p.set('timeout', String(timeout))
    } else {
      p.set('timeout', '0')
    }
    p.set('filter', MatrixClient.SYNC_FILTER)
    const data = await this.req<SyncResponse>('GET', '/_matrix/client/v3/sync?' + p.toString())
    if (data?.next_batch) this.syncToken = data.next_batch
    return data
  }
  resetSync(): void {
    this.syncToken = null
  }

  private txnId(): string {
    return 'atr' + Date.now() + '_' + this.txnSeq++
  }

  async sendMessage(
    roomId: string,
    body: string,
    extra?: Partial<RoomMessageContent>,
  ): Promise<{ event_id: string; txn: string }> {
    const txn = this.txnId()
    const content: RoomMessageContent = { msgtype: 'm.text', body: String(body ?? ''), ...(extra || {}) }
    const res = await this.req<{ event_id: string }>(
      'PUT',
      `/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/send/m.room.message/${txn}`,
      { body: content },
    )
    return { event_id: res.event_id, txn }
  }

  /** Send a custom (non-m.room.message) timeline event, e.g. an incident log entry. */
  async sendEvent(roomId: string, type: string, content: unknown): Promise<{ event_id: string }> {
    return this.req(
      'PUT',
      `/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/send/${encodeURIComponent(type)}/${this.txnId()}`,
      { body: content },
    )
  }

  /** React to a message: an m.reaction annotation. The key is what the chip
   *  shows — a unicode emoji, a ":shortcode:" custom emote, or a GIF URL.
   *  Custom emotes carry their image along (app.atrium.emote_url), because the
   *  shortcode only exists in the sender's own emote pack — receivers render
   *  the mxc image, not the name. */
  async sendReaction(
    roomId: string,
    targetEventId: string,
    key: string,
    emoteUrl?: string,
  ): Promise<{ event_id: string }> {
    const content: Record<string, unknown> = {
      'm.relates_to': { rel_type: 'm.annotation', event_id: targetEventId, key },
    }
    if (emoteUrl) content['app.atrium.emote_url'] = emoteUrl
    return this.sendEvent(roomId, 'm.reaction', content)
  }

  /** Redact an event — how a reaction is taken back (and messages deleted). */
  async redactEvent(roomId: string, eventId: string, reason?: string): Promise<{ event_id: string }> {
    return this.req(
      'PUT',
      `/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/redact/${encodeURIComponent(eventId)}/${this.txnId()}`,
      { body: reason ? { reason } : {} },
    )
  }

  /** Fetch a single event — resolves reply targets that fell out of the local
   *  timeline window. Null when it's not visible to us (or gone). */
  async getEvent(roomId: string, eventId: string): Promise<MatrixEvent | null> {
    try {
      return await this.req<MatrixEvent>(
        'GET',
        `/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/event/${encodeURIComponent(eventId)}`,
      )
    } catch {
      return null
    }
  }

  async messages(
    roomId: string,
    opts: { dir?: 'b' | 'f'; limit?: number; from?: string; filter?: unknown } = {},
  ): Promise<MessagesResponse> {
    const p = new URLSearchParams({ dir: opts.dir || 'b', limit: String(opts.limit || 30) })
    if (opts.from) p.set('from', opts.from)
    // A RoomEventFilter (e.g. {types:['m.room.message']}) makes the server page
    // PAST high-frequency noise — room heat + RTC membership churn share this
    // room's timeline and outnumber chat ~166:1, so an unfiltered fetch burns
    // its limit on machine events and surfaces almost no messages. URLSearchParams
    // encodes the JSON exactly once (pre-encoding would double-encode → 400).
    if (opts.filter !== undefined) p.set('filter', JSON.stringify(opts.filter))
    return this.req('GET', `/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/messages?` + p.toString())
  }

  async sendToDevice(type: string, messages: Record<string, Record<string, unknown>>): Promise<void> {
    await this.req('PUT', `/_matrix/client/v3/sendToDevice/${encodeURIComponent(type)}/${this.txnId()}`, {
      body: { messages },
    })
  }
}

export const matrix = new MatrixClient()
export type { MatrixEvent }
